<template>
  <nav
    v-if="subMenu.isVisible()"
    class="sub-menu"
    :class="subMenuClass"
  >
    <h2 class="sub-menu__header" @click="toggleCollapse">
      <span class="sub-menu__title">{{ $t(menuTitle) }}</span>
      <span
        v-if="subMenu.collapsed"
        class="material-icons sub-menu__title-button"
      >{{ isMobile ? 'keyboard_arrow_down' : 'chevron_right' }}</span>
      <span
        v-else
        class="material-icons sub-menu__title-button"
        >{{ isMobile ? 'keyboard_arrow_up' : 'chevron_left' }}</span>
    </h2>
    <div class="sub-menu-groups__wrapper">
      <div
        class="sub-menu-groups"
        v-for="(menu, menuGroupsIndex) in permittedSubMenus"
        :key="menuGroupsIndex"
      >
        <div
          v-if="!!menu.groupTitleLabel"
          class="sub-menu__group-title"
        >
          {{ $t(menu.groupTitleLabel) }}
        </div>
        <div
          v-if="!!menu.groupSubTitleLabel"
          class="sub-menu__group-sub-title"
        >
          {{ $t(menu.groupSubTitleLabel) }}
        </div>
        <ul class="list-unstyled mb-0">
          <li
            class="sub-menu__item"
            v-for="(menuItem, menuItemIndex) in menu.getItemsList()"
            :key="menuItemIndex"
            @click="collapseIfMobile"
          >
            <hr class="sub-menu__separator" v-if="menuItem.isSeparator">
            <a v-else-if="menuItem.isMenuGrouper">
              <span>{{ menuItem.title }}</span>
            </a>
            <router-link
              v-else
              class="rounded"
              :to="{ name: menuItem.routeName, params: menuItem.params }"
              :exact="menuItem.exact"
            >
              <span>{{ menuItem.title }}</span>
            </router-link>
          </li>
          <li
            v-if="!!menu.limitItemsLength"
            class="sub-menu__more-items-link"
          >
            <a
              class="btn-link"
              @click="menu.setLimitItemsLength(0)"
            >
              <span> {{ 'Exibir mais' }}</span>
            </a>
          </li>
        </ul>
        <hr
          v-if="showSeparator(menuGroupsIndex)"
          class="sub-menu__separator"
        >
      </div>
    </div>
  </nav>
</template>

<script>

import { mapGetters } from 'vuex';
import store from '@/store';
import mobileMixin from '@/services/mobileMixin';

export default {
  components: {},
  mixins: [mobileMixin],
  props: {
    menuTitle: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters([
      'subMenu',
    ]),
    permittedSubMenus() {
      return this.subMenu.items;
    },
    subMenuClass() {
      return {
        'sub-menu--hidden': !this.subMenu.isVisible(),
        'sub-menu--collapsed': this.subMenu.collapsed,
      };
    },
  },
  methods: {
    collapseIfMobile() {
      if (this.isMobile) {
        this.toggleCollapse();
      }
    },
    showSeparator(index) {
      return index < (this.menuGroups || []).length - 1;
    },
    toggleCollapse() {
      if (this.subMenu.collapsed) {
        this.$emit('onToggleCollapse');
      }

      store.dispatch('setSubMenuCollapsed', !this.subMenu.collapsed);
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"
@import "src/styles/defaults/mobile-first"
@import "src/styles/defaults/animation"

$title-height: 24px

.sub-menu-groups__wrapper
  +media-max-sm
    overflow: auto
    max-height: calc(80vh - (#{$sub-menu-padding-y} * 2) - #{$title-height})
    margin-top: 1rem

.sub-menu
  position: absolute
  top: 0
  left: $desktop-menu-width
  height: calc(#{$menu-height} - #{$footer-height})
  overflow-y: auto
  padding: $sub-menu-padding-y 0
  background-color: var(--sub-menu-bg-color)
  +media-min-md
    width: $desktop-submenu-width
    transition: $resize-duration

  +media-max-sm()
    background-color: var(--sub-menu-bg-xs-color)
    box-shadow: 0 4px 4px var(--color-black-38)
    top: $mobile-menu-height
    left: 0
    width: 100vw
    height: auto
    overflow-y: unset
    padding: 20px 0
  &--hidden
    left: -236px
    visibility: hidden
    opacity: 0

  &__title
    +media-min-md
      opacity: 1
      width: 100%
      // tslint-disable-next-line max-len
      transition: opacity $fade-in-duration, width $fade-in-duration $fade-in-duration
      transition-delay: calc(#{$resize-duration}/2)
  &__header
    align-items: flex-start
    cursor: pointer
    display: flex
    justify-content: space-between
    padding: 0 20px
    margin-bottom: 1.8rem
    font-size: 1.5em
    font-weight: bold
    +media-max-sm()
      margin-bottom: 0
      height: $title-height
  &__title-button
    width: 24px
    height: 24px
    +media-max-sm()
      top: calc(15px + 0.5rem - 6px)

  & .sub-menu-groups
    +media-min-md
      opacity: 1
      width: 100%
      // eslint-disable-next-line max-len
      transition: opacity $fade-in-duration $resize-duration, width 0s $resize-duration

    &-button
      width: 40px
      height: 40px
      padding: 6px
      display: flex
      flex-direction: row
      flex-wrap: wrap
      align-items: center
      justify-content: center
      position: absolute
      top: -6px
      right: 0

      & div
        width: 22px
        font-size: 1em
        cursor: pointer

  &--collapsed
    box-shadow: none
    overflow: hidden
    +media-min-md
      width: $desktop-menu-width
      transition: width $resize-duration
      transition-delay: $fade-out-duration
    +media-max-sm()
      width: 100vw
    & .sub-menu__title
      +media-min-md
        opacity: 0
        width: 0
        transition: opacity $fade-out-duration, width $fade-out-duration $fade-out-duration

    & .sub-menu-groups
      visibility: hidden
      opacity: 0
      width: 0
      transition: opacity $fade-out-duration, width $fade-out-duration $fade-out-duration
      +media-max-sm()
        margin-top: 0

    & .sub-menu__title
      +media-max-sm()
        opacity: 1
        visibility: unset
    & .sub-menu-groups
      +media-max-sm()
        height: 0

  &__group-title
    padding: 0 20px
    margin-bottom: 1.6em
    font-size: .85em
    font-weight: bold

  &__group-sub-title
    padding: 0 20px
    margin-bottom: 1.2em
    font-size: .75em
    text-transform: uppercase
    font-weight: bold

  &__item
    padding: 0 8px
    cursor: pointer
    color: var(--sub-menu-color)

    & a
      display: block
      padding: 8px 12px

      &:hover
        color: $color-menu-item-selected
        background: $color-menu-item-hover

      &.router-link-active
        color: $color-menu-item-hover
        background: $color-menu-item-selected

  &__more-items-link
    padding: 8px
    cursor: pointer

    & a
      padding: 12px
      font-size: .85em
      color: $color-link
      font-weight: 700

  &__separator
    border-top: 1px solid rgba(0, 0, 0, 0.12)
    margin-left: 20px
    margin-right: 20px
    margin-bottom: 30px
</style>
